// Sales Activities filter bar — FE-SCRM-024.
//
// Compact chip-style filters that mirror the BE-SCRM-023 list query
// shape: status, due bucket, type. Owner is intentionally a text/select
// surface, not chips — the owner list is unbounded.

import type {
  ActivityListQuery, ActivityStatus, ActivityType, DueBucket, SortKey,
} from "./types";
import { ACTIVITY_TYPE_META } from "./activityTypeMeta";


const STATUS_OPTIONS: Array<{ value: ActivityStatus; label: string }> = [
  { value: "open", label: "Open" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];


const DUE_OPTIONS: Array<{ value: DueBucket; label: string }> = [
  { value: "overdue", label: "Overdue" },
  { value: "today", label: "Today" },
  { value: "this_week", label: "This week" },
  { value: "upcoming", label: "Upcoming" },
];


const SORT_OPTIONS: Array<{ value: SortKey; label: string }> = [
  { value: "due_at", label: "Due soonest" },
  { value: "-due_at", label: "Due latest" },
  { value: "-created_at", label: "Newest" },
  { value: "created_at", label: "Oldest" },
];


export function ActivityFiltersBar({
  filters, sort, on_change,
}: {
  filters: ActivityListQuery;
  sort: SortKey;
  on_change: (next: {
    filters?: ActivityListQuery; sort?: SortKey;
  }) => void;
}) {
  return (
    <div
      data-testid="activities-filters-bar"
      className={[
        "flex flex-wrap items-center gap-x-3 gap-y-2",
        "border-b border-border-subtle pb-3",
      ].join(" ")}
    >
      <ChipGroup
        label="Status"
        testid="activities-filter-status"
        options={STATUS_OPTIONS}
        value={filters.status ?? "open"}
        onChange={(v) =>
          on_change({
            filters: { ...filters, status: v as ActivityStatus },
          })
        }
      />

      <ChipGroup
        label="Due"
        testid="activities-filter-due"
        options={[{ value: "", label: "Any" }, ...DUE_OPTIONS]}
        value={filters.due ?? ""}
        onChange={(v) =>
          on_change({
            filters: {
              ...filters,
              due: v ? (v as DueBucket) : undefined,
            },
          })
        }
      />

      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wide text-text-tertiary">
          Type
        </span>
        <select
          data-testid="activities-filter-type"
          value={filters.type ?? ""}
          onChange={(e) =>
            on_change({
              filters: {
                ...filters,
                type: e.target.value
                  ? (e.target.value as ActivityType) : undefined,
              },
            })
          }
          className={[
            "text-xs rounded border border-border-subtle",
            "bg-raised px-2 py-1 text-text-primary",
          ].join(" ")}
        >
          <option value="">All types</option>
          {ACTIVITY_TYPE_META.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
      </div>

      <div className="ml-auto flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wide text-text-tertiary">
          Sort
        </span>
        <select
          data-testid="activities-filter-sort"
          value={sort}
          onChange={(e) =>
            on_change({ sort: e.target.value as SortKey })
          }
          className={[
            "text-xs rounded border border-border-subtle",
            "bg-raised px-2 py-1 text-text-primary",
          ].join(" ")}
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
    </div>
  );
}


function ChipGroup({
  label, testid, options, value, onChange,
}: {
  label: string;
  testid: string;
  options: Array<{ value: string; label: string }>;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div data-testid={testid} className="flex items-center gap-1">
      <span className="text-[10px] uppercase tracking-wide text-text-tertiary mr-1">
        {label}
      </span>
      {options.map((o) => (
        <button
          key={o.value || "any"}
          type="button"
          aria-pressed={value === o.value}
          data-testid={`${testid}-${o.value || "any"}`}
          onClick={() => onChange(o.value)}
          className={[
            "px-2 py-0.5 text-xs rounded-full",
            value === o.value
              ? "bg-teal text-white"
              : "bg-recessed text-text-secondary hover:text-text-primary",
          ].join(" ")}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
